import type {StackEntryPayload, StackEntryServerStatus} from '../../../page-data/payloads/merge-box'
import type {StackMergeabilityPageData} from '../../../page-data/payloads/stack-mergeability'
import type {UseQueryResult} from '@github-ui/react-query'
import type {StackSectionStackEntry} from '../stack-section-types'
import {EffectiveEntryStatus} from '../../effective-entry-status'

// Non-dynamic statuses whose effectiveEntryStatus is fixed by the `merge_box`
// payload itself and never comes from `stack_mergeability` query data. These
// always win over query data (e.g. an AFTER entry stays 'AFTER').
const STATIC_STATUS_BY_SERVER_STATUS: Partial<Record<StackEntryServerStatus, EffectiveEntryStatus>> = {
  IRRELEVANT_MERGED: EffectiveEntryStatus.Merged,
  // TODO: remove this server status when graduating `pull_request_stacks_show_all_entries_statuses` feature flag
  IRRELEVANT_AFTER: EffectiveEntryStatus.After,
  IRRELEVANT_DRAFT: EffectiveEntryStatus.Draft,
  IRRELEVANT_QUEUED: EffectiveEntryStatus.Queued,
  MERGE_CONFLICTS: EffectiveEntryStatus.MergeConflicts,
  NEEDS_REBASE: EffectiveEntryStatus.NeedsRebase,
}

// Placeholder statuses used only while we wait for query data to arrive.
const SEED_PLACEHOLDER_BY_SERVER_STATUS: Partial<Record<StackEntryServerStatus, EffectiveEntryStatus>> = {
  BLOCKED_STACK: EffectiveEntryStatus.NotReady,
  NEEDS_MERGE_STATE: EffectiveEntryStatus.Loading,
}

// The fixed status for an entry, or undefined when the status is dynamic and
// should come from query data. A closed PR is always 'CLOSED', and an AFTER
// entry (stacked on top of CURRENT) is always 'AFTER'.
function staticEffectiveStatus(entry: StackEntryPayload): EffectiveEntryStatus | undefined {
  // TODO: remove this conditional and return value
  // when graduating `pull_request_stacks_show_all_entries_statuses` feature flag
  if (entry.position === 'AFTER' && entry.status === 'IRRELEVANT_AFTER') return EffectiveEntryStatus.After

  if (entry.pull.state === 'DRAFT') return EffectiveEntryStatus.Draft
  if (entry.pull.state === 'CLOSED') return EffectiveEntryStatus.Closed
  const byServerStatus = STATIC_STATUS_BY_SERVER_STATUS[entry.status]
  if (byServerStatus) return byServerStatus
  return undefined
}

function resolveIsInAutoMergeRange({
  mergeBoxValue,
  mergeBoxDataUpdatedAt,
  stackMergeabilityValue,
  stackMergeabilityDataUpdatedAt,
  effectiveEntryStatus,
}: {
  mergeBoxValue: boolean
  mergeBoxDataUpdatedAt: number
  // Undefined until the `stack_mergeability` query returns data for this entry.
  stackMergeabilityValue?: boolean
  stackMergeabilityDataUpdatedAt?: number
  effectiveEntryStatus: EffectiveEntryStatus
}): boolean {
  if (
    effectiveEntryStatus === EffectiveEntryStatus.Merged ||
    effectiveEntryStatus === EffectiveEntryStatus.Closed ||
    effectiveEntryStatus === EffectiveEntryStatus.Merging ||
    effectiveEntryStatus === EffectiveEntryStatus.Queued
  ) {
    return false
  }

  if (stackMergeabilityValue === undefined) return mergeBoxValue

  // These timestamps represent client cache writes rather than server snapshot versions, but comparing them
  // is the best available ordering signal until both endpoints provide shared server-side versioning.
  const isStackMergeabilityNewer = (stackMergeabilityDataUpdatedAt ?? 0) >= mergeBoxDataUpdatedAt
  if (isStackMergeabilityNewer) return stackMergeabilityValue
  return mergeBoxValue
}

/**
 * Hydrates the stack entries that come from the `merge_box` API by combining
 * them, by index, with the matching `stack_mergeability` query data (when it
 * exists). For each entry this does 3 things:
 *
 * 1. Convert each Stack Entry from a type of StackEntryPayload (from `merge_box` API) -> StackSectionStackEntry
 * 2. Merge the `.channels` and `.statusCheckChannels` fields from the `stack_mergeability`
 *    query data into a single AliveChannels object (null until that data arrives)
 * 3. Sets an effectiveEntryStatus. Non-dynamic statuses (closed, draft, merged,
 *    queued, after) come straight from the `merge_box` entry and always win over
 *    query data; otherwise it uses the `stack_mergeability` query data when
 *    present, falling back to a placeholder while that data loads.
 */
export function hydrateMergeBoxStackEntriesWithStackMergeabilityData(
  stackMergeabilityQueries: Array<UseQueryResult<StackMergeabilityPageData>>,
  mergeBoxDataUpdatedAt = 0,
): (mergeBoxStackEntries: StackEntryPayload[]) => StackSectionStackEntry[] {
  return mergeBoxStackEntries =>
    mergeBoxStackEntries.map((entry, index) => {
      const staticStatus = staticEffectiveStatus(entry)

      const stackMergeabilityQuery = stackMergeabilityQueries[index]

      if (stackMergeabilityQuery?.isError) {
        const effectiveEntryStatus = staticStatus ?? EffectiveEntryStatus.NotReady
        return {
          ...entry,
          pull: {
            ...entry.pull,
            isInAutoMergeRange: resolveIsInAutoMergeRange({
              mergeBoxValue: entry.pull.isInAutoMergeRange,
              mergeBoxDataUpdatedAt,
              effectiveEntryStatus,
            }),
          },
          channels: null,
          effectiveEntryStatus,
        }
      }

      if (stackMergeabilityQuery?.isLoading || !stackMergeabilityQuery || !stackMergeabilityQuery.data) {
        const effectiveEntryStatus =
          staticStatus ?? SEED_PLACEHOLDER_BY_SERVER_STATUS[entry.status] ?? EffectiveEntryStatus.Loading
        return {
          ...entry,
          pull: {
            ...entry.pull,
            isInAutoMergeRange: resolveIsInAutoMergeRange({
              mergeBoxValue: entry.pull.isInAutoMergeRange,
              mergeBoxDataUpdatedAt,
              effectiveEntryStatus,
            }),
          },
          channels: null,
          effectiveEntryStatus,
        }
      }

      const {channels, statusCheckChannels, effectiveEntryStatus, isInAutoMergeRange} = stackMergeabilityQuery.data
      const resolvedEffectiveEntryStatus = staticStatus ?? effectiveEntryStatus

      return {
        ...entry,
        pull: {
          ...entry.pull,
          isInAutoMergeRange: resolveIsInAutoMergeRange({
            mergeBoxValue: entry.pull.isInAutoMergeRange,
            mergeBoxDataUpdatedAt,
            stackMergeabilityValue: isInAutoMergeRange,
            stackMergeabilityDataUpdatedAt: stackMergeabilityQuery.dataUpdatedAt,
            effectiveEntryStatus: resolvedEffectiveEntryStatus,
          }),
        },
        channels: {...channels, ...statusCheckChannels},
        effectiveEntryStatus: resolvedEffectiveEntryStatus,
      }
    })
}
